import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import { WipConfig } from '../../types';
import { DeleteIconButton } from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';

type DeleteConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onClickDelete: () => Promise<void>;
  isLoading: boolean;
};
export const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onClickDelete,
  isLoading,
}: DeleteConfirmationModalProps) => {
  const toast = useToast();
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete item</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb="24px">Are you sure you want to delete this item?</Text>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            onClick={async () => {
              try {
                await onClickDelete();
              } catch (err) {
                toast({
                  ...errorToastConfig,
                  id: 'deleteError',
                  title: 'Error deleting item. Please try again.',
                });
              } finally {
                onClose();
              }
            }}
            color="white"
            isLoading={isLoading}
          >
            Delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

type DeleteArrayPropertyButtonProps = {
  wipConfig: WipConfig;
  basePropertyKey: string;
  idx: number;
  useConfirmationModal?: boolean;
  saveOnDelete?: boolean;
};
/**
 * This component should be used to delete entities that are contained in an array
 */
export const DeleteArrayPropertyButton = ({
  wipConfig,
  basePropertyKey,
  idx,
  useConfirmationModal = false,
  saveOnDelete = false,
}: DeleteArrayPropertyButtonProps) => {
  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose,
  } = useDisclosure();

  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const {
    formProps,
    capitalProvidersMap,
    setCapitalProvidersMap,
    saveAndUpdateConfigCreditAgreementWip,
  } = wipConfig;

  const deleteProperty = async () => {
    const propertySection = _.cloneDeep(
      getIn(formProps.values, basePropertyKey),
    );
    const [removedItem] = propertySection.splice(idx, 1);
    if (
      basePropertyKey === 'facilityMechanics.parties.capitalProviders' &&
      removedItem
    ) {
      const omittedCapitalProviderMap = _.omit(
        capitalProvidersMap,
        removedItem.entity.id,
      );
      setCapitalProvidersMap(omittedCapitalProviderMap);
    }

    if (saveOnDelete) {
      await saveAndUpdateConfigCreditAgreementWip({
        wipConfig,
        propertyKey: basePropertyKey,
        updatedProperty: propertySection,
        setIsSaveLoading: setIsDeleteLoading,
      });
    } else {
      formProps.setFieldValue(basePropertyKey, propertySection);
    }

    onDeleteConfirmationModalClose();
  };

  return (
    <>
      <DeleteIconButton
        ariaLabel="delete"
        label="Delete"
        onClick={(event) => {
          if (event) {
            event.stopPropagation();
          }

          if (useConfirmationModal) {
            onDeleteConfirmationModalOpen();
          } else {
            deleteProperty();
          }
        }}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteConfirmationModalOpen}
        onClose={onDeleteConfirmationModalClose}
        onClickDelete={deleteProperty}
        isLoading={isDeleteLoading}
      />
    </>
  );
};
